// Phase 78az / 78ba (Feb-2026) — Google API settings page.
// Per-usage toggles grouped by Google API + daily / monthly usage chart.
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { CurrencyInr, Warning, ChartBar, Clock } from '@phosphor-icons/react';
import { refreshGoogleApiFlags } from '../../hooks/useGoogleApiFlags';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';

export default function GoogleApiSettings() {
  const [registry, setRegistry] = useState([]);
  const [groups, setGroups] = useState({});
  const [values, setValues] = useState({});
  const [original, setOriginal] = useState({});
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('daily'); // 'daily' | 'monthly'

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [flagsRes, usageRes] = await Promise.all([
        api.get('/admin/google-api-flags'),
        api.get('/admin/google-api-usage', { params: { days: 30, months: 6 } }),
      ]);
      setRegistry(flagsRes.data.registry || []);
      setGroups(flagsRes.data.groups || {});
      setValues(flagsRes.data.values || {});
      setOriginal(flagsRes.data.values || {});
      setUsage(usageRes.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load Google API settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const dirtyKeys = Object.keys(values).filter(k => values[k] !== original[k]);
  const isDirty = dirtyKeys.length > 0;

  // Group registry by `group` key, preserving registry order.
  const groupedRegistry = useMemo(() => {
    const out = {};
    registry.forEach(f => {
      if (!out[f.group]) out[f.group] = [];
      out[f.group].push(f);
    });
    return out;
  }, [registry]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = {};
      dirtyKeys.forEach(k => { patch[k] = values[k]; });
      const { data } = await api.put('/admin/google-api-flags', { flags: patch });
      setValues(data.values || {});
      setOriginal(data.values || {});
      await refreshGoogleApiFlags();
      toast.success('Google API settings saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed — super-admin access required.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setValues({ ...original });

  if (loading) {
    return <div className="text-sm text-[#666] uppercase tracking-widest" data-testid="google-api-loading">Loading Google API settings…</div>;
  }
  if (error) {
    return (
      <div className="bg-[#FF3333]/10 border border-[#FF3333]/30 px-4 py-3 text-sm text-[#FF3333]">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="google-api-settings">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <CurrencyInr size={24} weight="bold" className="text-[#002FA7]" />
          <h1 className="text-2xl font-bold tracking-tight">Google API</h1>
        </div>
        <p className="text-sm text-[#666] mt-1">
          Each toggle below corresponds to one billable Google Maps API
          call-site. Turn off any feature you can live without to immediately
          cut Cloud-Billing spend — no redeploy needed.
        </p>
      </div>

      {/* Warning banner */}
      <div className="flex gap-3 bg-[#FF8C00]/10 border border-[#FF8C00]/30 px-4 py-3">
        <Warning size={20} weight="bold" className="text-[#FF8C00] shrink-0 mt-0.5" />
        <div className="text-xs text-[#7A4A00]">
          <strong>Only super-admin can save changes.</strong> Disabling a feature
          falls back to a free alternative (straight-line distance, haversine ETA,
          manual address entry). Live vehicle GPS positions are NEVER affected by
          these flags — they continue to stream in real time.
        </div>
      </div>

      {/* Phase 78bb — One-click backfill for legacy trips */}
      <BackfillEtaCard />

      {/* Usage Chart Card */}
      <UsageChartCard
        usage={usage}
        groups={groups}
        view={view}
        setView={setView}
      />

      {/* Flag groups */}
      {Object.entries(groupedRegistry).map(([groupKey, flags]) => {
        const meta = groups[groupKey] || { label: groupKey, api: '', color: '#002FA7' };
        const onCount = flags.filter(f => values[f.key]).length;
        const groupTotal = usage?.totals?.by_group?.[groupKey] || 0;
        return (
          <div
            key={groupKey}
            className="bg-white border border-[#E5E5E5]"
            data-testid={`google-api-group-${groupKey}`}
          >
            <div
              className="flex items-center justify-between gap-3 px-4 md:px-6 py-3 border-b border-[#E5E5E5]"
              style={{ borderTop: `3px solid ${meta.color}` }}
            >
              <div>
                <h2 className="text-base font-bold tracking-tight">{meta.label}</h2>
                <p className="text-xs text-[#666] mt-0.5">{meta.api}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-[#999]">
                  {onCount}/{flags.length} ON
                </p>
                <p className="text-xs font-bold text-[#002FA7]">
                  {groupTotal.toLocaleString('en-IN')} calls · 30 d
                </p>
              </div>
            </div>
            {flags.map((flag, i) => {
              const on = values[flag.key];
              const callCount = usage?.totals?.by_flag?.[flag.key] || 0;
              return (
                <div
                  key={flag.key}
                  data-testid={`google-api-flag-${flag.key}`}
                  className={`flex flex-col md:flex-row md:items-start gap-3 px-4 md:px-6 py-4 ${i > 0 ? 'border-t border-[#E5E5E5]' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold">{flag.label}</h3>
                      {!on && (
                        <span className="text-[10px] uppercase tracking-widest bg-[#FF3333]/10 text-[#FF3333] px-2 py-0.5">
                          Disabled
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#666] mt-1">{flag.description}</p>
                    {flag.frequency && (
                      <p className="text-[11px] text-[#7A4A00] mt-1 flex items-center gap-1">
                        <Clock size={12} weight="bold" />
                        <span><strong>Frequency:</strong> {flag.frequency}</span>
                      </p>
                    )}
                    {flag.screens?.length > 0 && (
                      <p className="text-[10px] uppercase tracking-widest text-[#999] mt-1">
                        Used in: {flag.screens.join(' · ')}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0 min-w-[120px]">
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-widest text-[#666]">
                        {on ? 'On' : 'Off'}
                      </span>
                      <Switch
                        checked={!!on}
                        onCheckedChange={(v) => setValues(prev => ({ ...prev, [flag.key]: v }))}
                        data-testid={`google-api-toggle-${flag.key}`}
                      />
                    </div>
                    <p className="text-[10px] uppercase tracking-widest text-[#999]">
                      {callCount.toLocaleString('en-IN')} calls · 30 d
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Sticky action bar */}
      <div className="flex items-center justify-between gap-3 sticky bottom-0 bg-white border-t border-[#E5E5E5] px-4 py-3 -mx-6 -mb-6">
        <div className="text-xs text-[#666]">
          {isDirty
            ? <><strong className="text-[#FF8C00]">{dirtyKeys.length} unsaved change{dirtyKeys.length > 1 ? 's' : ''}</strong> · Hit Save to push to live.</>
            : 'All settings up to date.'}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={!isDirty || saving}
            onClick={handleReset}
            className="rounded-none"
            data-testid="google-api-reset-btn"
          >
            Reset
          </Button>
          <Button
            disabled={!isDirty || saving}
            onClick={handleSave}
            className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-none"
            data-testid="google-api-save-btn"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ----- Usage chart card --------------------------------------------------
function UsageChartCard({ usage, groups, view, setView }) {
  const series = view === 'daily' ? (usage?.daily || []) : (usage?.monthly || []);
  const groupKeys = Object.keys(groups || {});

  // Reshape: each row → { period, polyline, eta, optimize, ... }
  const data = useMemo(() => series.map(r => {
    const out = { period: view === 'daily' ? (r.date?.slice(5) || '') : (r.month || '') };
    groupKeys.forEach(k => { out[k] = r.by_group?.[k] || 0; });
    return out;
  }), [series, groupKeys, view]);

  const totals = usage?.totals || {};

  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid="google-api-usage-chart">
      <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3 border-b border-[#E5E5E5]">
        <div className="flex items-center gap-2">
          <ChartBar size={20} weight="bold" className="text-[#002FA7]" />
          <h2 className="text-base font-bold tracking-tight">API usage</h2>
        </div>
        <div className="flex border border-[#E5E5E5]">
          <button
            onClick={() => setView('daily')}
            className={`px-3 py-1 text-[10px] uppercase tracking-widest ${view === 'daily' ? 'bg-[#002FA7] text-white' : 'text-[#666] hover:bg-[#F3F3F3]'}`}
            data-testid="usage-view-daily"
          >Daily · 30 d</button>
          <button
            onClick={() => setView('monthly')}
            className={`px-3 py-1 text-[10px] uppercase tracking-widest ${view === 'monthly' ? 'bg-[#002FA7] text-white' : 'text-[#666] hover:bg-[#F3F3F3]'}`}
            data-testid="usage-view-monthly"
          >Monthly · 6 mo</button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-px bg-[#E5E5E5]">
        <SummaryTile label="Today" value={totals.today || 0} />
        <SummaryTile label="Last 7 days" value={totals.last_7d || 0} />
        <SummaryTile label="Last 30 days" value={totals.last_30d || 0} />
      </div>

      {/* Chart */}
      <div className="p-4 md:p-6" style={{ height: 320 }}>
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-[#999] uppercase tracking-widest">
            No usage recorded yet.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#F3F3F3" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#666' }} interval={view === 'daily' ? 2 : 0} />
              <YAxis tick={{ fontSize: 11, fill: '#666' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ border: '1px solid #E5E5E5', borderRadius: 0, fontSize: 12 }}
                labelStyle={{ fontWeight: 'bold' }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {groupKeys.map(k => (
                <Bar
                  key={k}
                  dataKey={k}
                  stackId="usage"
                  fill={groups[k]?.color || '#002FA7'}
                  name={groups[k]?.label || k}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="bg-white px-4 py-3">
      <p className="text-[10px] uppercase tracking-widest text-[#999]">{label}</p>
      <p className="text-xl font-bold text-[#002FA7] mt-1">{Number(value).toLocaleString('en-IN')}</p>
      <p className="text-[10px] uppercase tracking-widest text-[#666]">API calls</p>
    </div>
  );
}


// Phase 78bb — admin tool: one-click bulk compute Google ETA for trips
// that have coords but no cached ETA yet.
function BackfillEtaCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post('/admin/trips/backfill-eta', null, { params: { limit: 500 } });
      setResult(data);
      toast.success(`Backfilled ETA for ${data.updated} trips · ${data.skipped} skipped · ${data.errors} errors`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-[#002FA7]/5 border border-[#002FA7]/20 px-4 py-4 flex items-start gap-3" data-testid="eta-backfill-card">
      <CurrencyInr size={20} weight="bold" className="text-[#002FA7] shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-bold">Backfill ETA cache for legacy trips</h3>
        <p className="text-xs text-[#666] mt-1">
          Computes Google ETA + distance ONCE for up to 500 trips that have
          pickup/drop coordinates but no cached value. Future reads come from
          the cached field — never from a live Google call.
        </p>
        {result && (
          <p className="text-[11px] text-[#7A4A00] mt-2">
            Scanned <strong>{result.scanned}</strong> · Updated <strong>{result.updated}</strong> · Skipped <strong>{result.skipped}</strong> · Errors <strong>{result.errors}</strong>
          </p>
        )}
      </div>
      <Button
        disabled={running}
        onClick={handleRun}
        className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-none shrink-0"
        data-testid="eta-backfill-btn"
      >
        {running ? 'Running…' : 'Run backfill'}
      </Button>
    </div>
  );
}
